import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { OccCell } from "@/components/OccCell";
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, X, Send, Copy, Trash2,
  AlertTriangle, LayoutGrid, Columns, Save, FileStack, Euro, Clock,
} from "lucide-react";

const mondayOf = (d) => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x.toISOString().slice(0, 10);
};
const shortDate = (iso) => iso.slice(8, 10) + "/" + iso.slice(5, 7);
const eur = (n) => "€" + (Math.round((n || 0) * 100) / 100).toLocaleString("el-GR");
const hrsLabel = (n) => (Math.round((n || 0) * 10) / 10) + "ω";

export default function ScheduleBuilder() {
  const qc = useQueryClient();
  const [week, setWeek] = useState(mondayOf(new Date()));
  const [view, setView] = useState("week");
  const [dayIdx, setDayIdx] = useState(0);
  const [deptF, setDeptF] = useState("");
  const [edit, setEdit] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["tt-schedule", week],
    queryFn: async () => (await api.get(`/team/schedule?week_start=${week}`)).data,
  });
  const { data: templates = [] } = useQuery({
    queryKey: ["tt-templates"],
    queryFn: async () => (await api.get("/team/schedule/templates")).data,
    enabled: !!data?.can_edit,
  });

  const dates = data?.dates || [];
  const { user } = useAuth();
  const showOcc = ["owner", "hr", "manager"].includes(user?.role) && dates.length > 0;
  const { data: occ } = useQuery({
    queryKey: ["pms-occupancy", dates[0]?.date, dates[dates.length - 1]?.date],
    queryFn: async () => (await api.get(`/pms/occupancy?date_from=${dates[0].date}&date_to=${dates[dates.length - 1].date}`)).data,
    enabled: showOcc,
  });
  const occFor = (iso) => (occ?.days || []).find((x) => x.date === iso);
  const members = data?.members || [];
  const depts = data?.departments || [];
  const shifts = data?.shifts || [];
  const canEdit = !!data?.can_edit;
  const canCost = !!data?.can_view_cost;
  const conflicts = data?.conflicts || [];
  const deptName = (id) => depts.find((d) => d.id === id)?.name || "—";
  const rolesOf = (id) => depts.find((d) => d.id === id)?.roles || [];

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["tt-schedule"] }); };
  const shiftWeek = (n) => { const d = new Date(week); d.setDate(d.getDate() + n); setWeek(mondayOf(d)); };

  const saveShift = useMutation({
    mutationFn: async (s) => s.id ? (await api.put(`/team/shifts/${s.id}`, s)).data : (await api.post("/team/shifts", s)).data,
    onSuccess: () => { toast.success("Αποθηκεύτηκε"); invalidate(); setEdit(null); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const delShift = useMutation({
    mutationFn: async (id) => (await api.delete(`/team/shifts/${id}`)).data,
    onSuccess: () => { toast.success("Διαγράφηκε"); invalidate(); setEdit(null); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const publish = useMutation({
    mutationFn: async () => (await api.post(`/team/schedule/publish?week_start=${week}`)).data,
    onSuccess: (r) => { toast.success(`Δημοσιεύτηκε · ${r.emails_sent} email`); invalidate(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const copyPrev = useMutation({
    mutationFn: async () => { const d = new Date(week); d.setDate(d.getDate() - 7); return (await api.post(`/team/schedule/copy?from_week=${mondayOf(d)}&to_week=${week}`)).data; },
    onSuccess: (r) => { toast.success(`Αντιγράφηκαν ${r.created} βάρδιες`); invalidate(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const clearWk = useMutation({
    mutationFn: async () => (await api.delete(`/team/schedule?week_start=${week}`)).data,
    onSuccess: (r) => { toast.success(`Καθαρίστηκαν ${r.deleted} βάρδιες`); invalidate(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const saveTpl = useMutation({
    mutationFn: async (name) => (await api.post("/team/schedule/templates", { name, week_start: week })).data,
    onSuccess: (r) => { toast.success(`Πρότυπο "${r.name}" (${r.count} βάρδιες)`); qc.invalidateQueries({ queryKey: ["tt-templates"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const applyTpl = useMutation({
    mutationFn: async (id) => (await api.post("/team/schedule/apply-template", { template_id: id, week_start: week })).data,
    onSuccess: (r) => { toast.success(`Προστέθηκαν ${r.created} βάρδιες`); invalidate(); setTplOpen(false); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const delTpl = useMutation({
    mutationFn: async (id) => (await api.delete(`/team/schedule/templates/${id}`)).data,
    onSuccess: () => { toast.success("Το πρότυπο διαγράφηκε"); qc.invalidateQueries({ queryKey: ["tt-templates"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const shiftsFor = (memberId, date) => shifts.filter((s) => (s.member_id || "") === memberId && s.date === date);
  const openShifts = (date) => shifts.filter((s) => s.is_open && s.date === date);
  const conflictFor = (memberId) => conflicts.filter((c) => c.member_id === memberId);

  const visibleMembers = useMemo(() => members.filter((m) => !deptF || m.department_id === deptF), [members, deptF]);
  const shownDates = view === "week" ? dates : dates.slice(dayIdx, dayIdx + 1);

  const newShift = (memberId, date, open = false) => {
    const m = members.find((x) => x.id === memberId);
    const dId = open ? deptF : (m?.department_id || "");
    setEdit({ member_id: open ? "" : memberId, is_open: open, date, start: "09:00", end: "17:00", break_min: 0, department_id: dId, role_id: "", role_name: "", note: "" });
  };

  const totalsRow = data?.day_totals || {};
  const memTotals = data?.member_totals || {};

  return (
    <div className="space-y-5" data-testid="schedule-builder-page" data-wide>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Team &amp; Time</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2">
            <CalendarDays className="h-7 w-7" /> Πρόγραμμα Βαρδιών
          </h1>
          <p className="text-[#6F6A60] text-sm mt-1">
            {data?.week_start} – {data?.week_end} · {hrsLabel(data?.week_hours)}
            {canCost && data?.week_cost != null ? ` · ${eur(data.week_cost)}` : ""}
            {" · "}
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${data?.status === "published" ? "bg-[#E2ECE4] text-[#3F6B4C]" : "bg-[#F3EADF] text-[#9A6A3A]"}`}>
              {data?.status === "published" ? "Δημοσιευμένο" : "Πρόχειρο"}
            </span>
            {canEdit && data?.unpublished > 0 && <span className="ml-2 text-[#9A6A3A] text-[11px]" data-testid="unpublished-count">{data.unpublished} μη δημοσιευμένες</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="sched-prev" onClick={() => shiftWeek(-7)} className="h-10 w-10 rounded-xl border border-[#E3DED4] bg-white flex items-center justify-center hover:border-[#B9AE9A]"><ChevronLeft className="h-4 w-4" /></button>
          <input data-testid="sched-week" type="date" value={week} onChange={(e) => setWeek(mondayOf(e.target.value))} className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2.5 text-sm outline-none focus:border-[#45423C]" />
          <button data-testid="sched-next" onClick={() => shiftWeek(7)} className="h-10 w-10 rounded-xl border border-[#E3DED4] bg-white flex items-center justify-center hover:border-[#B9AE9A]"><ChevronRight className="h-4 w-4" /></button>
          <button data-testid="sched-today" onClick={() => setWeek(mondayOf(new Date()))} className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2.5 text-sm font-semibold text-[#6F6A60] hover:border-[#B9AE9A]">Τρέχουσα</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-xl border border-[#E3DED4] bg-white overflow-hidden">
          <button data-testid="view-week" onClick={() => setView("week")} className={`px-3 py-2 text-sm inline-flex items-center gap-1.5 ${view === "week" ? "bg-[#45423C] text-white" : "text-[#6F6A60]"}`}><LayoutGrid className="h-4 w-4" /> Εβδομάδα</button>
          <button data-testid="view-day" onClick={() => setView("day")} className={`px-3 py-2 text-sm inline-flex items-center gap-1.5 ${view === "day" ? "bg-[#45423C] text-white" : "text-[#6F6A60]"}`}><Columns className="h-4 w-4" /> Ημέρα</button>
        </div>
        {view === "day" && (
          <select data-testid="day-picker" value={dayIdx} onChange={(e) => setDayIdx(Number(e.target.value))} className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm">
            {dates.map((d, i) => <option key={d.date} value={i}>{d.label} {shortDate(d.date)}</option>)}
          </select>
        )}
        <select data-testid="dept-filter" value={deptF} onChange={(e) => setDeptF(e.target.value)} className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm">
          <option value="">Όλα τα τμήματα</option>
          {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap ml-auto">
            <button data-testid="copy-prev" onClick={() => copyPrev.mutate()} disabled={copyPrev.isPending} className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm font-semibold text-[#45423C] hover:border-[#B9AE9A] inline-flex items-center gap-1.5"><Copy className="h-4 w-4" /> Αντιγραφή προηγ.</button>
            <button data-testid="save-template" onClick={() => { const n = window.prompt("Όνομα προτύπου:"); if (n) saveTpl.mutate(n); }} className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm font-semibold text-[#45423C] hover:border-[#B9AE9A] inline-flex items-center gap-1.5"><Save className="h-4 w-4" /> Αποθήκ. προτύπου</button>
            <button data-testid="apply-template" onClick={() => setTplOpen(true)} className="rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm font-semibold text-[#45423C] hover:border-[#B9AE9A] inline-flex items-center gap-1.5"><FileStack className="h-4 w-4" /> Πρότυπα</button>
            <button data-testid="clear-week" onClick={() => { if (window.confirm("Διαγραφή όλων των βαρδιών της εβδομάδας;")) clearWk.mutate(); }} className="rounded-xl border border-[#E7CEC6] bg-white px-3 py-2 text-sm font-semibold text-[#A1493A] hover:border-[#A1493A] inline-flex items-center gap-1.5"><Trash2 className="h-4 w-4" /> Καθαρισμός</button>
            <button data-testid="publish-btn" onClick={() => publish.mutate()} disabled={publish.isPending} className="rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:bg-[#56524A] inline-flex items-center gap-1.5 disabled:opacity-50"><Send className="h-4 w-4" /> Δημοσίευση</button>
          </div>
        )}
      </div>

      {conflicts.length > 0 && (
        <div className="rounded-xl border border-[#E7CEC6] bg-[#FBF1ED] p-3" data-testid="conflicts-banner">
          <div className="flex items-center gap-2 text-[#A1493A] font-semibold text-sm mb-1"><AlertTriangle className="h-4 w-4" /> {conflicts.length} ζητήματα</div>
          <div className="flex flex-wrap gap-2">
            {conflicts.slice(0, 12).map((c, i) => (
              <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full ${c.severity === "error" ? "bg-[#F6E2DD] text-[#A1493A]" : "bg-[#F3EADF] text-[#9A6A3A]"}`}>
                {c.member_name}: {c.detail}{c.date ? ` (${shortDate(c.date)})` : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                <th className="px-3 py-2.5 sticky left-0 bg-[#45423C] z-10 min-w-[160px]">Μέλος</th>
                {shownDates.map((d) => (
                  <th key={d.date} data-testid={`sched-day-${d.date}`} className={`px-2 py-2.5 text-center min-w-[120px] ${d.holiday ? "bg-[#C86235] text-white" : d.dow >= 5 ? "bg-[#56524A]" : ""}`}>
                    {d.label}<span className={`block text-[9px] font-normal ${d.holiday ? "text-white/80" : "text-white/50"}`}>{shortDate(d.date)}</span>
                    {d.holiday && <span className="block text-[9px] font-semibold normal-case tracking-normal mt-0.5" data-testid={`sched-holiday-${d.date}`}>★ {d.holiday} · +75%</span>}
                  </th>
                ))}
                <th className="px-3 py-2.5 text-right min-w-[90px]">Σύνολο</th>
              </tr>
              {showOcc && occ && (
                <tr className="bg-[#F7F5F1] border-b border-[#E3DED4]" data-testid="schedule-occ-row">
                  <th className="px-3 py-1.5 text-left text-[11px] font-semibold text-[#45423C] sticky left-0 bg-[#F7F5F1] z-10">Πληρότητα βιλών · άτομα</th>
                  {shownDates.map((d) => {
                    const o = occFor(d.date);
                    return <th key={d.date} className="px-1 py-1 font-normal">{o ? <OccCell d={o} /> : null}</th>;
                  })}
                  <th />
                </tr>
              )}
            </thead>
            <tbody>
              {/* open shifts row */}
              <tr className="border-t border-[#E3DED4] bg-[#FBF8F3]">
                <td className="px-3 py-2 sticky left-0 bg-[#FBF8F3] z-10 font-semibold text-[#9A6A3A]">Ανοιχτές βάρδιες</td>
                {shownDates.map((d) => (
                  <td key={d.date} className="px-2 py-2 align-top" data-testid={`open-cell-${d.date}`}>
                    <div className="flex flex-col gap-1">
                      {openShifts(d.date).map((s) => (
                        <ShiftChip key={s.id} s={s} open canEdit={canEdit} canCost={canCost} roleName={s.role_name} onClick={() => canEdit && setEdit({ ...s })} />
                      ))}
                      {canEdit && <AddBtn testid={`add-open-${d.date}`} onClick={() => newShift("", d.date, true)} />}
                    </div>
                  </td>
                ))}
                <td className="px-3 py-2 text-right text-[#9A6A3A] tabular">{hrsLabel(memTotals["__open__"]?.hours)}</td>
              </tr>

              {visibleMembers.map((m) => {
                const mc = conflictFor(m.id);
                return (
                  <tr key={m.id} className="border-t border-[#E3DED4] hover:bg-[#FAF8F4]" data-testid={`sched-row-${m.id}`}>
                    <td className="px-3 py-2 sticky left-0 bg-white z-10">
                      <div className="font-medium text-[#45423C] flex items-center gap-1.5">
                        {m.name}
                        {m.is_minor && <span className="text-[10px] text-[#A1493A] font-semibold" title="Ανήλικος">⚠</span>}
                        {mc.length > 0 && <span className="text-[10px] text-[#A1493A]" title={mc.map((c) => c.detail).join(", ")}><AlertTriangle className="h-3 w-3 inline" /></span>}
                      </div>
                      <div className="text-[11px] text-[#8A8577]">{deptName(m.department_id)}</div>
                    </td>
                    {shownDates.map((d) => (
                      <td key={d.date} className="px-2 py-2 align-top" data-testid={`cell-${m.id}-${d.date}`}>
                        <div className="flex flex-col gap-1">
                          {shiftsFor(m.id, d.date).map((s) => (
                            <ShiftChip key={s.id} s={s} canEdit={canEdit} canCost={canCost} roleName={s.role_name} member={m} onClick={() => canEdit && setEdit({ ...s })} />
                          ))}
                          {canEdit && <AddBtn testid={`add-${m.id}-${d.date}`} onClick={() => newShift(m.id, d.date)} />}
                        </div>
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right tabular font-semibold text-[#45423C]" data-testid={`mem-total-${m.id}`}>
                      {hrsLabel(memTotals[m.id]?.hours)}
                      {canCost && memTotals[m.id]?.cost != null && <div className="text-[11px] font-normal text-[#6F6A60]">{eur(memTotals[m.id].cost)}</div>}
                    </td>
                  </tr>
                );
              })}
              {visibleMembers.length === 0 && (
                <tr><td colSpan={shownDates.length + 2} className="px-4 py-8 text-center text-[#6F6A60]">{isLoading ? "Φόρτωση…" : "Κανένα μέλος."}</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#E3DED4] bg-[#F1EDE6] font-semibold text-[#45423C]">
                <td className="px-3 py-2 sticky left-0 bg-[#F1EDE6] z-10">Σύνολα ημέρας</td>
                {shownDates.map((d) => (
                  <td key={d.date} className="px-2 py-2 text-center tabular text-xs" data-testid={`day-total-${d.date}`}>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-[#6E7A63]" />{hrsLabel(totalsRow[d.date]?.hours)}</span>
                    {canCost && totalsRow[d.date]?.cost != null && <div className="font-normal text-[#6F6A60] inline-flex items-center gap-1 ml-1"><Euro className="h-3 w-3" />{Math.round(totalsRow[d.date].cost)}</div>}
                  </td>
                ))}
                <td className="px-3 py-2 text-right tabular">{hrsLabel(data?.week_hours)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {edit && (
        <ShiftModal edit={edit} setEdit={setEdit} depts={depts} rolesOf={rolesOf} members={members}
          onSave={() => saveShift.mutate(edit)} onDelete={() => edit.id && delShift.mutate(edit.id)}
          saving={saveShift.isPending} />
      )}

      {tplOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="tpl-modal" onClick={() => setTplOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl text-[#45423C]">Εφαρμογή προτύπου</h2>
              <button data-testid="tpl-close" onClick={() => setTplOpen(false)}><X className="h-5 w-5 text-[#6F6A60]" /></button>
            </div>
            {templates.length === 0 && <p className="text-sm text-[#6F6A60]">Δεν υπάρχουν πρότυπα. Αποθήκευσε την τρέχουσα εβδομάδα ως πρότυπο.</p>}
            <div className="space-y-2">
              {templates.map((t) => (
                <div key={t.id} className="w-full rounded-xl border border-[#E3DED4] px-4 py-3 text-sm hover:border-[#B9AE9A] flex items-center justify-between gap-2">
                  <button data-testid={`tpl-${t.id}`} onClick={() => applyTpl.mutate(t.id)} className="flex-1 text-left font-medium text-[#45423C]">{t.name}</button>
                  <span className="text-xs text-[#8A8577]">εφαρμογή →</span>
                  <button data-testid={`tpl-del-${t.id}`} onClick={() => { if (window.confirm(`Διαγραφή προτύπου "${t.name}";`)) delTpl.mutate(t.id); }} className="text-[#A1493A] hover:text-[#991B1B]"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const AddBtn = ({ onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="w-full rounded-md border border-dashed border-[#E3DED4] py-1 text-[#B9B0A2] hover:border-[#6E7A63] hover:text-[#6E7A63] transition-colors flex items-center justify-center">
    <Plus className="h-3.5 w-3.5" />
  </button>
);

const ShiftChip = ({ s, open, canEdit, roleName, onClick }) => (
  <button data-testid={`shift-${s.id}`} onClick={onClick} disabled={!canEdit}
    className={`w-full rounded-md px-1.5 py-1 text-left text-[11px] leading-tight transition-colors ${open ? "bg-[#F3EADF] text-[#9A6A3A] hover:bg-[#ecdcc8]" : s.status === "published" ? "bg-[#E2ECE4] text-[#3F6B4C] hover:bg-[#d4e3d7]" : "bg-[#EEF0EA] text-[#6E7A63] hover:bg-[#e0e4d9]"} ${canEdit ? "cursor-pointer" : "cursor-default"}`}>
    <span className="font-semibold tabular">{(s.start || "").slice(0, 5)}–{(s.end || "").slice(0, 5)}</span>
    {roleName && <span className="block truncate opacity-80">{roleName}</span>}
    {s.status !== "published" && !open && <span className="block text-[9px] opacity-70">πρόχειρο</span>}
  </button>
);

function ShiftModal({ edit, setEdit, rolesOf, members, onSave, onDelete, saving }) {
  const roles = rolesOf(edit.department_id);
  const set = (k, v) => setEdit({ ...edit, [k]: v });
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50 overflow-y-auto" data-testid="shift-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-[#45423C]">{edit.id ? "Επεξεργασία βάρδιας" : edit.is_open ? "Ανοιχτή βάρδια" : "Νέα βάρδια"}</h2>
          <button onClick={() => setEdit(null)}><X className="h-5 w-5 text-[#6F6A60]" /></button>
        </div>
        <div className="space-y-3 text-sm">
          {!edit.is_open && (
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Μέλος</label>
              <select data-testid="sm-member" value={edit.member_id} onChange={(e) => { const m = members.find((x) => x.id === e.target.value); set("member_id", e.target.value); if (m) setEdit((p) => ({ ...p, member_id: e.target.value, department_id: m.department_id || p.department_id })); }} className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2 bg-white">
                <option value="">—</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Έναρξη</label>
              <input data-testid="sm-start" type="time" value={edit.start} onChange={(e) => set("start", e.target.value)} className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Λήξη</label>
              <input data-testid="sm-end" type="time" value={edit.end} onChange={(e) => set("end", e.target.value)} className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Ρόλος</label>
              <select data-testid="sm-role" value={edit.role_id} onChange={(e) => { const r = roles.find((x) => x.id === e.target.value); set("role_id", e.target.value); setEdit((p) => ({ ...p, role_id: e.target.value, role_name: r?.name || "" })); }} className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2 bg-white">
                <option value="">—</option>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Διάλειμμα (λεπτά)</label>
              <input data-testid="sm-break" type="number" min="0" value={edit.break_min} onChange={(e) => set("break_min", parseInt(e.target.value) || 0)} className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2" />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#6F6A60] font-semibold">Σημείωση</label>
            <input data-testid="sm-note" value={edit.note} onChange={(e) => set("note", e.target.value)} className="mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2" />
          </div>
        </div>
        <div className="mt-6 flex gap-3">
          <button data-testid="sm-save" onClick={onSave} disabled={saving || (!edit.is_open && !edit.member_id)} className="flex-1 rounded-xl bg-[#45423C] text-white py-2.5 text-sm font-semibold disabled:opacity-50">Αποθήκευση</button>
          {edit.id && <button data-testid="sm-delete" onClick={onDelete} className="rounded-xl border border-[#A1493A] text-[#A1493A] px-4 py-2.5 text-sm inline-flex items-center gap-1"><Trash2 className="h-4 w-4" /></button>}
          <button onClick={() => setEdit(null)} className="rounded-xl border border-[#E3DED4] px-4 py-2.5 text-sm">Άκυρο</button>
        </div>
      </div>
    </div>
  );
}
